import { useCallback, useMemo } from "react";

import type { McpInitializeResult } from "@/lib/mcp/types";
import {
  type McpSessionStore,
  useMcpSessionStore,
} from "@/stores/mcpSessionStore";

export function useMcpInitialization(projectId?: string, enabled = true) {
  const sessionSelector = useCallback(
    (state: McpSessionStore) =>
      projectId && enabled ? state.getSession(projectId) : undefined,
    [projectId, enabled],
  );

  const initializationSelector = useCallback(
    (state: McpSessionStore) =>
      projectId && enabled ? state.getInitialization(projectId) : undefined,
    [projectId, enabled],
  );

  const session = useMcpSessionStore(sessionSelector);
  const initialization = useMcpSessionStore(initializationSelector);

  const data = enabled ? initialization : undefined;
  const isLoading = useMemo(() => {
    if (!enabled || !projectId) {
      return false;
    }

    if (!session) {
      return true;
    }

    if (session.status === "error") {
      return false;
    }

    if (initialization) {
      return false;
    }

    return session.status === "idle" || session.status === "connecting";
  }, [enabled, projectId, session, initialization]);

  const error = useMemo(() => {
    if (!enabled || !projectId) {
      return undefined;
    }

    if (!session || session.status !== "error" || !session.error) {
      return undefined;
    }

    return new Error(session.error);
  }, [enabled, projectId, session]);

  return {
    data,
    isLoading,
    error,
  };
}

export function hasCapability(
  initResult: McpInitializeResult | undefined,
  capability: keyof McpInitializeResult["capabilities"],
): boolean {
  return !!initResult?.capabilities?.[capability];
}
